import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useCurrentUser from '../hooks/useCurrentUser';
import {
  getQuotationById,
  sendQuotation,
  duplicateQuotation,
  deleteQuotation,
  getBranchTaxes,
} from '../api';
import QuoteItemsTable from '../components/QuoteItemsTable';
import { hasPermission } from '../utils/permissions';
import { getQuotationStatusLabel, isDraftQuotationStatus } from '../utils/quotationStatus';
import InlineLoading from '../components/InlineLoading';
import { notify } from '../utils/alertBus';
import useConfirmDialog from '../hooks/useConfirmDialog';
import useQuotationSaleConversion from '../hooks/useQuotationSaleConversion';
import {
  getQuotationPdfAssetKey,
  preloadQuotationPdfImages,
  resolveQuotationClientPricingCategoryLabels,
  saveQuotationPdf,
} from '../utils/quotationPdf';

const QuotationDetailPage = () => {
  const { branchId, id } = useParams();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [taxes, setTaxes] = useState([]);
  const [pdfAssetStatus, setPdfAssetStatus] = useState('idle');
  const [pdfAssetProgress, setPdfAssetProgress] = useState({ completed: 0, total: 0 });
  const [isSavingPdf, setIsSavingPdf] = useState(false);
  const pdfPreparationRef = useRef(null);
  const pdfPreparationKeyRef = useRef('');
  const pdfAssetsRef = useRef({});
  const { user } = useCurrentUser();
  const canUpdateQuotation = hasPermission(user, 'quotation.update');
  const canCreateQuotation = hasPermission(user, 'quotation.create');
  const canDeleteQuotation = hasPermission(user, 'quotation.delete');
  const canReadTaxes = hasPermission(user, 'tax.read') || hasPermission(user, 'tax.exclude');
  const { confirm } = useConfirmDialog();
  const { requestConversion, conversionDialog } = useQuotationSaleConversion(branchId);

  const fetchQuotation = async () => {
    try {
      const data = await getQuotationById(branchId, id);
      setQuotation(data);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Error al cargar la cotización');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotation();
  }, [branchId, id]);

  const beginPdfImagePreparation = useCallback((targetQuotation, { force = false } = {}) => {
    if (!targetQuotation) return Promise.resolve({});
    const assetKey = getQuotationPdfAssetKey(targetQuotation);
    if (!force && pdfPreparationKeyRef.current === assetKey && pdfPreparationRef.current) {
      return pdfPreparationRef.current;
    }

    pdfPreparationKeyRef.current = assetKey;
    pdfAssetsRef.current = {};
    setPdfAssetStatus('preparing');
    setPdfAssetProgress({
      completed: 0,
      total: Array.isArray(targetQuotation.items) ? targetQuotation.items.length : 0,
    });

    const preparation = preloadQuotationPdfImages(targetQuotation.items, {
      onProgress: (completed, total) => {
        if (pdfPreparationKeyRef.current !== assetKey) return;
        setPdfAssetProgress({ completed, total });
      },
    })
      .then((assets) => {
        if (pdfPreparationKeyRef.current === assetKey) {
          pdfAssetsRef.current = assets;
          setPdfAssetStatus('ready');
        }
        return assets;
      })
      .catch((err) => {
        console.error('Error preparando imagenes para PDF:', err);
        if (pdfPreparationKeyRef.current === assetKey) {
          setPdfAssetStatus('error');
        }
        return {};
      });

    pdfPreparationRef.current = preparation;
    return preparation;
  }, []);

  useEffect(() => {
    if (!quotation) return undefined;
    let cancelled = false;
    const prepare = () => {
      if (!cancelled) {
        beginPdfImagePreparation(quotation);
      }
    };

    let idleHandle = null;
    let timeoutHandle = null;
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      idleHandle = window.requestIdleCallback(prepare, { timeout: 1000 });
    } else {
      timeoutHandle = window.setTimeout(prepare, 0);
    }

    return () => {
      cancelled = true;
      if (idleHandle !== null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle !== null) {
        window.clearTimeout(timeoutHandle);
      }
    };
  }, [quotation, beginPdfImagePreparation]);

  const ensurePdfImageAssets = useCallback(() => {
    if (!quotation) return Promise.resolve({});
    const assetKey = getQuotationPdfAssetKey(quotation);
    if (pdfPreparationKeyRef.current === assetKey && pdfAssetStatus === 'ready') {
      return Promise.resolve(pdfAssetsRef.current || {});
    }
    return beginPdfImagePreparation(quotation, { force: pdfAssetStatus === 'error' });
  }, [beginPdfImagePreparation, pdfAssetStatus, quotation]);

  useEffect(() => {
    if (!branchId || !canReadTaxes) {
      setTaxes([]);
      return;
    }
    let isMounted = true;
    const loadTaxes = async () => {
      try {
        const data = await getBranchTaxes(branchId);
        if (!isMounted) return;
        setTaxes(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!isMounted) return;
        console.error(err);
        setTaxes([]);
      }
    };
    loadTaxes();
    return () => {
      isMounted = false;
    };
  }, [branchId, canReadTaxes]);

  const handleSend = async () => {
    if (!canUpdateQuotation) return;
    if (!isDraftQuotationStatus(quotation?.status)) {
      notify('La cotización está en solo lectura.');
      return;
    }
    if (!quotation?.Client?.email) {
      notify('El cliente no tiene correo registrado, no se puede enviar.');
      return;
    }
    try {
      await sendQuotation(branchId, id, {});
      notify('Correo enviado exitosamente');
      fetchQuotation();
    } catch (err) {
      console.error(err);
      notify('No se pudo enviar el correo, verifique el correo del cliente e intente nuevamente');
    }
  };

  const handleDuplicate = async () => {
    if (!canCreateQuotation) return;
    try {
      const resp = await duplicateQuotation(branchId, id);
      navigate(`/branches/${branchId}/quotations/${resp.quotation.id}`);
    } catch (err) {
      console.error(err);
      notify(err?.response?.data?.message || 'No se pudo duplicar la cotización');
    }
  };

  const handleConvert = async () => {
    if (!canUpdateQuotation) return;
    try {
      const response = await requestConversion(id, quotation?.totalAmount);
      if (!response) return;
      const purchaseId = response?.purchase?.id;
      if (purchaseId) {
        notify('Cotización convertida. Redirigiendo a registrar pago.');
        navigate(`/branches/${branchId}/purchases/${purchaseId}/receipt`);
        return;
      }
      notify('Cotización convertida, pero no se encontró la venta creada.');
      fetchQuotation();
    } catch (err) {
      console.error(err);
      notify(err?.response?.data?.message || 'No se pudo convertir la cotización');
    }
  };

  const handleDelete = async () => {
    if (!canDeleteQuotation) return;
    const approved = await confirm({
      title: 'Eliminar cotización',
      message: '¿Eliminar esta cotización?',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    try {
      await deleteQuotation(branchId, id);
      navigate(`/branches/${branchId}/quotations`);
    } catch (err) {
      console.error(err);
      notify('No se pudo eliminar la cotización');
    }
  };

  if (loading) {
    return (
      <p className="p-4">
        <InlineLoading label="Cargando cotización" />
      </p>
    );
  }
  if (error) return <p className="p-4 text-red-600">{error}</p>;
  if (!quotation) return <p className="p-4">No se encontró la cotización.</p>;

  const excludedTaxIds = Array.isArray(quotation.excludedTaxIds) ? quotation.excludedTaxIds : [];
  const taxNameById = new Map(
    (taxes || []).map((tax) => [Number(tax.id), tax.name || `Impuesto ${tax.id}`])
  );
  const excludedTaxLabels = excludedTaxIds
    .map((id) => taxNameById.get(Number(id)) || `#${id}`)
    .filter(Boolean);
  const clientPricingCategoryLabels = resolveQuotationClientPricingCategoryLabels(quotation);

  const handleSavePdf = async () => {
    if (!quotation || isSavingPdf) return;
    setIsSavingPdf(true);
    try {
      const imageAssets = await ensurePdfImageAssets();
      saveQuotationPdf({
        quotation,
        excludedTaxLabels,
        imageAssets,
      });
    } catch (err) {
      console.error('Error generando PDF:', err);
      notify('No se pudo generar el PDF.');
    } finally {
      setIsSavingPdf(false);
    }
  };

  const pdfButtonLabel = isSavingPdf ? 'Guardando...' : 'Guardar PDF';
  const pdfButtonTitle = pdfAssetStatus === 'preparing' && pdfAssetProgress.total > 0
    ? `Preparando imagenes para PDF (${pdfAssetProgress.completed}/${pdfAssetProgress.total})`
    : 'Guardar cotización como PDF';

  return (
    <>
      {conversionDialog}
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-5xl mx-auto bg-white shadow rounded p-6">
        <div className="flex justify-between items-start gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold">Cotización #{quotation.id}</h1>
            <p className="text-sm text-gray-600">
              Estado: <span className="font-semibold">{getQuotationStatusLabel(quotation.status)}</span>
            </p>
            <p className="text-xs text-gray-500">
              Actualizada: {new Date(quotation.updatedAt).toLocaleString()}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canUpdateQuotation && isDraftQuotationStatus(quotation.status) && (
              <button
                onClick={() => navigate(`/branches/${branchId}/quotations/${quotation.id}/edit`)}
                className="px-3 py-2 bg-blue-600 text-white rounded text-sm"
              >
                Editar
              </button>
            )}
            {canCreateQuotation && (
              <button onClick={handleDuplicate} className="px-3 py-2 bg-yellow-500 text-white rounded text-sm">
                Duplicar
              </button>
            )}
            {canUpdateQuotation && isDraftQuotationStatus(quotation.status) && (
              <button
                onClick={handleSend}
                disabled={!quotation.Client?.email}
                className="px-3 py-2 bg-green-600 text-white rounded text-sm disabled:opacity-60"
                title={!quotation.Client?.email ? 'El cliente no tiene correo' : undefined}
              >
                Enviar
              </button>
            )}
            {canUpdateQuotation && quotation.status === 'sent' && (
              <button onClick={handleConvert} className="px-3 py-2 bg-teal-600 text-white rounded text-sm">
                Convertir
              </button>
            )}
            {canDeleteQuotation && isDraftQuotationStatus(quotation.status) && quotation.isDeleted !== true && (
              <button onClick={handleDelete} className="px-3 py-2 bg-red-600 text-white rounded text-sm">
                Eliminar
              </button>
            )}
            <button
              onClick={handleSavePdf}
              disabled={isSavingPdf}
              className="px-3 py-2 bg-gray-200 rounded text-sm min-w-[112px] disabled:opacity-60"
              title={pdfButtonTitle}
            >
              {pdfButtonLabel}
            </button>
            <button
              onClick={() => navigate(`/branches/${branchId}/quotations`)}
              className="px-3 py-2 bg-gray-200 rounded text-sm"
            >
              Volver
            </button>
          </div>
        </div>

        {quotation.Client && (
          <div className="mt-4 border rounded p-4 bg-gray-50">
            <h2 className="font-semibold mb-1">Cliente</h2>
            <p className="text-sm">{quotation.Client.name}</p>
            {quotation.Client.rut && <p className="text-sm text-gray-600">RUT: {quotation.Client.rut}</p>}
            {quotation.Client.email && <p className="text-sm text-gray-600">Email: {quotation.Client.email}</p>}
            {!quotation.Client.email && (
              <p className="text-xs text-red-600">Sin correo: no se puede enviar por email.</p>
            )}
          </div>
        )}

        {clientPricingCategoryLabels.length > 0 && (
          <div className="mt-4 border border-emerald-100 rounded p-4 bg-emerald-50">
            <h2 className="font-semibold mb-1 text-emerald-900">
              {clientPricingCategoryLabels.length === 1
                ? 'Categoría de descuento aplicada'
                : 'Categorías de descuento aplicadas'}
            </h2>
            <p className="text-sm text-emerald-800">{clientPricingCategoryLabels.join(', ')}</p>
          </div>
        )}

        <h2 className="text-lg font-semibold mt-6 mb-2">Productos</h2>
        <QuoteItemsTable
          items={quotation.items}
          itemsSubtotal={quotation.itemsSubtotal}
          orderDiscountAmount={quotation.orderDiscountAmount}
          totalAmount={quotation.totalAmount}
          netTotal={quotation.netTotal}
          taxBreakdown={quotation.taxBreakdown}
          excludedTaxLabels={excludedTaxLabels}
        />
      </div>
    </div>
    </>
  );
};

export default QuotationDetailPage;
